import {
  isDirectorySearchLeaf,
  nodeMatchesSearchCriterion,
  type DirectorySearchCriterion,
} from './directorySearch.utils';
import { isFolderLikeKind } from './directoryTree.utils';
import type { DirectoryTreeNode } from './types';

const GROUPED_SEARCH_CRITERIA: DirectorySearchCriterion[] = ['title', 'prefix'];

export function filterDirectoryTree(
  nodes: DirectoryTreeNode[],
  searchTerm: string,
  criterion: DirectorySearchCriterion = 'module'
): DirectoryTreeNode[] {
  const term = searchTerm.trim().toLowerCase();
  if (!term) {
    return nodes;
  }

  if (GROUPED_SEARCH_CRITERIA.includes(criterion)) {
    return groupMatchingLeavesByImmediateParent(nodes, term, criterion);
  }

  return nodes.reduce<DirectoryTreeNode[]>((acc, node) => {
    const filteredChildren = node.children
      ? filterDirectoryTree(node.children, searchTerm, criterion)
      : [];
    const matches = nodeMatchesSearchCriterion(node, term, criterion);

    if (matches || filteredChildren.length > 0) {
      let children: DirectoryTreeNode[] | undefined;
      if (filteredChildren.length > 0) {
        children = filteredChildren;
      } else if (matches) {
        children = node.children;
      }
      acc.push({ ...node, children });
    }

    return acc;
  }, []);
}

/**
 * Groups file/record matches under their immediate parent (folder, or module when the
 * item sits at the module root), so the module is never shown wrapping a folder result.
 */
function groupMatchingLeavesByImmediateParent(
  nodes: DirectoryTreeNode[],
  term: string,
  criterion: DirectorySearchCriterion
): DirectoryTreeNode[] {
  const groups = new Map<string, { parent: DirectoryTreeNode; items: DirectoryTreeNode[] }>();

  const collect = (children: DirectoryTreeNode[], immediateParent: DirectoryTreeNode) => {
    for (const node of children) {
      if (isDirectorySearchLeaf(node) && nodeMatchesSearchCriterion(node, term, criterion)) {
        const group = groups.get(immediateParent.id) ?? { parent: immediateParent, items: [] };
        group.items.push(node);
        groups.set(immediateParent.id, group);
      }

      if (node.children?.length) {
        const nextParent = isFolderLikeKind(node.kind) ? node : immediateParent;
        collect(node.children, nextParent);
      }
    }
  };

  for (const module of nodes) {
    if (module.children?.length) {
      collect(module.children, module);
    }
  }

  return [...groups.values()].map(({ parent, items }) => ({ ...parent, children: items }));
}

export function collectDirectoryNodeIds(nodes: DirectoryTreeNode[]): string[] {
  const ids: string[] = [];

  const walk = (list: DirectoryTreeNode[]) => {
    for (const node of list) {
      ids.push(node.id);
      if (node.children?.length) {
        walk(node.children);
      }
    }
  };

  walk(nodes);
  return ids;
}

export function collectExpandedIdsForSearch(nodes: DirectoryTreeNode[]): string[] {
  const ids: string[] = [];

  const walk = (list: DirectoryTreeNode[]) => {
    for (const node of list) {
      if (node.children?.length) {
        ids.push(node.id);
        walk(node.children);
      }
    }
  };

  walk(nodes);
  return ids;
}
